import React, { useContext, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import queryString from 'query-string-for-all';

import {
  Info,
  Text,
  TextTypes,
  Title,
  ActionType,
} from '@prisjakt/ui-review-form';
import T from '@prisjakt/ui-translate';
import MultiRadio, { Variants } from '@prisjakt/ui-review-form/src/multi-radio';
import { AppContext } from '@prisjakt/ui-app-context/src/app-context';
import StoreInfoCard from '../reviews/components/store-info-card';
import PageContent from '../../components/page-content';

import { graphql } from '../../globals/api/requests';
import editVerifiedShopReviewQuery from './edit-verified-shop-review-query.graphql';
import updateVerifiedShopReviewMutation from './update-verified-shop-review-mutation.graphql';
import deleteVerifiedShopReviewMutation from './delete-verified-shop-review-mutation.graphql';

import { EditVerfiedShopReviewQuery } from '../../types';
import {
  Errors,
  GraphQLError,
  Questions,
  ShopReviewFormData,
} from '../reviews/types';

import * as S from './styles';
import {
  ShopReviewType,
  storeReviewDeleted,
  storeReviewEdited,
  storeReviewPublished,
} from '../reviews/tracking';
import { useReviewErrorToast } from '../reviews/hooks/use-review-error-toast';

type Props = {
  data: EditVerfiedShopReviewQuery;
};

const findAnswerToQuestion = (
  questionId: string,
  data: EditVerfiedShopReviewQuery
): number | undefined =>
  data?.shopReview?.answers?.find(answer => answer?.id === questionId)?.answer;

const EditVerifiedShopReviewPage = ({ data }: Props) => {
  const { t, market, linkTo } = useContext(AppContext);
  const dispatchReviewErrorToast = useReviewErrorToast();
  const [pending, setPending] = useState<boolean>(false);
  const location = useLocation();
  const { push } = useHistory();
  const { shop_id: shopId, code } = queryString.parse(location.search);
  const linkToItem = { id: shopId, pathName: data?.store?.pathName };

  const handleSubmit = async (formData: ShopReviewFormData) => {
    if (!data?.store?.id) {
      dispatchReviewErrorToast(Errors.INVALID_PARAMETER);
      return;
    }
    setPending(true);

    const {
      [Questions.Rating]: rating,
      [Questions.Review]: reviewText,
      ...customQuestions
    } = formData;

    const answers = Object.keys(customQuestions).map(questionId => ({
      id: questionId,
      answer: Number(customQuestions[questionId]),
    }));

    const scaledRating = Number(rating) * 2;

    const response = await graphql(updateVerifiedShopReviewMutation, {
      reviewId: data?.shopReview?.id,
      rating: scaledRating,
      reviewText,
      answers,
      code,
    });

    if (response.errors?.length > 0) {
      response.errors.forEach((error: GraphQLError) => {
        dispatchReviewErrorToast(error.code);
      });
      setPending(false);
      return;
    }

    if (data?.shopReview?.isPublished) {
      storeReviewEdited({
        type: ShopReviewType.PURCHASE,
        storeId: data?.store?.id,
        storeName: data?.store?.name,
      });
    } else if (response.data?.editShopReview?.review?.isPublished) {
      storeReviewPublished({
        type: ShopReviewType.PURCHASE,
        storeId: data?.store?.id,
        storeName: data?.store?.name,
      });
    }

    push(linkTo('store', linkToItem, { notification: 'verify-success' }));
  };

  const handleDelete = async () => {
    setPending(true);

    const response = await graphql(deleteVerifiedShopReviewMutation, {
      id: data?.shopReview?.id,
      code,
    });

    if (response.errors?.length > 0) {
      response.errors.forEach((error: GraphQLError) => {
        dispatchReviewErrorToast(error.code);
      });
      setPending(false);
    } else {
      if (data?.store?.id) {
        storeReviewDeleted({
          type: ShopReviewType.PURCHASE,
          storeId: data?.store?.id,
          storeName: data?.store?.name,
        });
      }
      push(linkTo('store', linkToItem, { notification: 'delete-success' }));
    }
  };

  return (
    <PageContent>
      <S.PurchaseReviewForm
        onSubmit={handleSubmit}
        actionType={
          !data?.shopReview?.isPublished ? ActionType.PUBLISH : ActionType.EDIT
        }
        onDelete={handleDelete}
        pending={pending}
      >
        <S.Header>{t('reviews', 'store.form-title')}</S.Header>
        <StoreInfoCard
          storeId={data?.store?.id}
          storeName={data?.store?.name || ''}
          featured={data?.store?.featured}
          hasLogo={data?.store?.hasLogo}
          reviewCount={data?.store?.userReviewSummary.count}
        />
        {market.shopOfTheYear?.year && (
          <T domain="reviews">store.soty-info.md</T>
        )}
        <div>
          <Title>{t('reviews', 'store.question-rating')}</Title>
          <MultiRadio
            name={Questions.Rating}
            variant={Variants.rate}
            options={[1, 2, 3, 4, 5]}
            optionValues={[
              t('reviews', 'rating.terrible'),
              t('reviews', 'rating.bad'),
              t('reviews', 'rating.ok'),
              t('reviews', 'rating.good'),
              t('reviews', 'rating.excellent'),
            ]}
            required
            currentValue={
              data?.shopReview?.rating
                ? data?.shopReview?.rating / 2
                : undefined
            }
          />
        </div>
        <div>
          <Title>{t('reviews', 'store.question-review')}</Title>
          <Text
            type={TextTypes.TextArea}
            name={Questions.Review}
            placeholder={t('reviews', 'store.question-review-placeholder')}
            defaultValue={data?.shopReview?.reviewText}
            minLength={20}
            required
          />

          <Info>
            <T domain="reviews">dialog.rules.md</T>
          </Info>
        </div>
        <div>
          <Title>
            {t('reviews', 'store.question-recommendation', {
              name: data?.store?.name || '',
            })}
          </Title>

          <MultiRadio
            name={Questions.Recommend}
            variant={Variants.scale}
            options={[1, 2, 3, 4, 5]}
            optionValues={[
              t('reviews', 'store.answer-recommendation-1'),
              t('reviews', 'store.answer-recommendation-2'),
              t('reviews', 'store.answer-recommendation-3'),
              t('reviews', 'store.answer-recommendation-4'),
              t('reviews', 'store.answer-recommendation-5'),
            ]}
            labels={[
              t('reviews', 'store.answer-recommendation-1'),
              '',
              t('reviews', 'store.answer-recommendation-3'),
              '',
              t('reviews', 'store.answer-recommendation-5'),
            ]}
            required
            currentValue={findAnswerToQuestion(Questions.Recommend, data)}
          />
        </div>

        <div>
          <Title>
            {t('reviews', 'store.question-delivery-time')}
            <S.Optional>{t('reviews', 'form.question-optional')}</S.Optional>
          </Title>

          <MultiRadio
            name={Questions.DeliveryAccuracy}
            variant={Variants.scale}
            options={[1, 2, 3, 4, 5]}
            optionValues={[
              t('reviews', 'store.answer-delivery-time-1'),
              t('reviews', 'store.answer-delivery-time-2'),
              t('reviews', 'store.answer-delivery-time-3'),
              t('reviews', 'store.answer-delivery-time-4'),
              t('reviews', 'store.answer-delivery-time-5'),
            ]}
            labels={[
              t('reviews', 'store.answer-delivery-time-1'),
              '',
              t('reviews', 'store.answer-delivery-time-3'),
              '',
              t('reviews', 'store.answer-delivery-time-5'),
            ]}
            currentValue={findAnswerToQuestion(
              Questions.DeliveryAccuracy,
              data
            )}
          />
        </div>

        <div>
          <Title>
            {t('reviews', 'store.question-customer-service')}

            <S.Optional>{t('reviews', 'form.question-optional')}</S.Optional>
          </Title>
          <MultiRadio
            name={Questions.CustomerService}
            variant={Variants.scale}
            options={[1, 2, 3, 4, 5]}
            optionValues={[
              t('reviews', 'store.answer-customer-service-1'),
              t('reviews', 'store.answer-customer-service-2'),
              t('reviews', 'store.answer-customer-service-3'),
              t('reviews', 'store.answer-customer-service-4'),
              t('reviews', 'store.answer-customer-service-5'),
            ]}
            labels={[
              t('reviews', 'store.answer-customer-service-1'),
              '',
              t('reviews', 'store.answer-customer-service-3'),
              '',
              t('reviews', 'store.answer-customer-service-5'),
            ]}
            currentValue={findAnswerToQuestion(Questions.CustomerService, data)}
          />
        </div>

        <div>
          <Title>{t('reviews', 'store.question-sponsored')}</Title>
          <Info>
            <T domain="reviews">store.question-sponsored-info.md</T>
          </Info>
          <MultiRadio
            name={Questions.PaidReview}
            variant={Variants.boolean}
            options={[0, 1]}
            required
            currentValue={findAnswerToQuestion(Questions.PaidReview, data)}
          />
        </div>
      </S.PurchaseReviewForm>
    </PageContent>
  );
};

type QueryVariables = {
  // eslint-disable-next-line camelcase
  shop_id: string;
  code: string;
  // eslint-disable-next-line camelcase
  review_id: string;
};

EditVerifiedShopReviewPage.variables = (props: QueryVariables) => {
  const reviewId = `i${props.review_id.replace('i', '')}`;
  return { shopId: props.shop_id, reviewId };
};

EditVerifiedShopReviewPage.graphQL = editVerifiedShopReviewQuery;

export default EditVerifiedShopReviewPage;
